/**
 * DatePicker component using shadcn/ui Calendar and Popover
 * Provides a date picker with calendar popup
 */

import * as React from 'react'
import { format } from 'date-fns'
import { IconCalendar } from '@tabler/icons-react'
import { Calendar } from '@/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

interface DatePickerProps {
  value?: string // ISO date string (YYYY-MM-DD) or empty string
  onChange: (value: string) => void
  placeholder?: string
  disabled?: boolean
  id?: string
  'aria-invalid'?: boolean
  className?: string
}

/**
 * DatePicker component
 * Converts between string (YYYY-MM-DD) and Date objects
 */
export function DatePicker({
  value,
  onChange,
  placeholder = 'Chọn ngày',
  disabled = false,
  id,
  'aria-invalid': ariaInvalid,
  className,
}: DatePickerProps) {
  const [open, setOpen] = React.useState(false)

  // Convert string to Date object
  const date = React.useMemo(() => {
    if (!value || value === '') return undefined
    const parsed = new Date(value)
    return isNaN(parsed.getTime()) ? undefined : parsed
  }, [value])

  // Handle date selection
  const handleSelect = (selectedDate: Date | undefined) => {
    if (selectedDate) {
      // Format as YYYY-MM-DD for input compatibility
      const year = selectedDate.getFullYear()
      const month = String(selectedDate.getMonth() + 1).padStart(2, '0')
      const day = String(selectedDate.getDate()).padStart(2, '0')
      onChange(`${year}-${month}-${day}`)
      setOpen(false)
    }
  }

  // Format date for display
  const displayValue = date
    ? format(date, 'dd/MM/yyyy')
    : placeholder

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          variant="outline"
          className={cn(
            'w-full justify-start text-left font-normal min-h-[44px]',
            !date && 'text-muted-foreground',
            ariaInvalid && 'border-destructive',
            className
          )}
          disabled={disabled}
          aria-invalid={ariaInvalid}
        >
          <IconCalendar className="mr-2 h-4 w-4" stroke={1.5} />
          {displayValue}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={date}
          onSelect={handleSelect}
          initialFocus
          className="rounded-md border-0"
        />
      </PopoverContent>
    </Popover>
  )
}

